import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';

type UtmKeys = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_content' | 'utm_term' | 'gclid' | 'fbclid';
type UtmBag = Partial<Record<UtmKeys, string>> & {
  ts?: number;
  lp?: string;      // landing page path
  ref?: string;     // document.referrer host
};

@Injectable({ providedIn: 'root' })
export class UtmService {
  // Keys we care about (add/remove as you see fit)
  private readonly keys: UtmKeys[] = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'fbclid',
  ];

  private readonly FIRST_KEY = 'sf_utm_first';
  private readonly LAST_KEY  = 'sf_utm_last';
  private readonly COOKIE_KEY = 'sf_utm'; // optional cookie handoff
  private readonly TTL_DAYS = 90; // attribution window

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  /** Parse UTM params from a URL string (or current location) */
  parseFromUrl(url?: string): UtmBag {
    if (!isPlatformBrowser(this.platformId)) return {};
    const searchParams = new URLSearchParams(url ? (new URL(url, location.origin)).search : location.search);

    const bag: UtmBag = {};
    this.keys.forEach(k => {
      const v = searchParams.get(k);
      if (v) bag[k] = v;
    });
    return bag;
  }

  /** Save first-touch and last-touch; never overwrite first-touch if already set */
  persistFromUrl(url?: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const utm = this.parseFromUrl(url);
    if (!this.hasAnyValue(utm)) return; // nothing to store

    utm.ts = Date.now();
    utm.lp = location.pathname + location.search;
    utm.ref = document.referrer ? new URL(document.referrer).host : '';

    // First-touch
    const first = this.getLocal(this.FIRST_KEY);
    if (!first || this.isExpired(first.ts)) {
      this.setLocal(this.FIRST_KEY, utm);
    }

    // Last-touch (always update on each valid arrival with UTM/clid)
    this.setLocal(this.LAST_KEY, utm);

    // Optional: cookie for server-side handoff (Symfony can read it)
    this.setCookie(this.COOKIE_KEY, JSON.stringify({ first: this.getLocal(this.FIRST_KEY), last: utm }), this.TTL_DAYS);
  }

  /** Public getters */
  getFirst(): UtmBag | null { return this.getLocal(this.FIRST_KEY); }
  getLast(): UtmBag | null  { return this.getLocal(this.LAST_KEY);  }

  /** Helpers */
  private hasAnyValue(bag: UtmBag): boolean {
    return this.keys.some(k => !!bag[k]);
  }
  private isExpired(ts?: number): boolean {
    if (!ts) return true;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays > this.TTL_DAYS;
  }
  private getLocal(key: string): UtmBag | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) as UtmBag : null;
    } catch { return null; }
  }
  private setLocal(key: string, value: UtmBag): void {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
  }
  private setCookie(name: string, value: string, days: number): void {
    try {
      const d = new Date(); d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
    } catch {}
  }
}
